import { Loading, NavigateButton, Table, Text } from '@/components';
import { OrganizationSearchUrlSchema } from '@/features/tenant-manager/models';
import { useExpandedRowKeys, useParsedSearchParams } from '@/hooks';
import { useMessage } from '@/providers';
import {
  TABLE_BUTTON_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_TREE_CODE_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
  getRecursiveIds,
  transformToDataWithChildren,
} from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBulkUpdateExcludedFlags,
  useOrganizationListRecursive,
} from '../../hooks';
import { BulkUpdateExcludedFlagsFormModal } from './BulkUpdateExcludedFlagsFormModal';
import { OrganizationName } from './OrganizationName';

export const OrganizationsTable: FC = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const {
    setSearchParams,
    referenceFiscalYear,
    showExcludedOptions,
    page: pageNumber,
  } = useParsedSearchParams(OrganizationSearchUrlSchema);
  const organizations = useOrganizationListRecursive({
    page: pageNumber > 0 ? pageNumber : 1,
    referenceFiscalYear: referenceFiscalYear
      ? Number(referenceFiscalYear)
      : undefined,
    showExcludedOptions: showExcludedOptions === 'true',
  });

  const [openModal, setOpenModal] = useState(false);
  const { mutateAsync: bulkUpdateExcludedFlags } = useBulkUpdateExcludedFlags();
  const { expandedRowKeys, setExpandedRowKeys, onExpand } =
    useExpandedRowKeys();

  if (organizations.isLoading || !organizations.data) {
    return <Loading />;
  }

  const dataSource = transformToDataWithChildren(organizations.data.results);

  const _openAllExpanded = () => {
    setExpandedRowKeys(getRecursiveIds(dataSource));
  };

  return (
    <>
      <Table
        dataSource={dataSource}
        rowKey="id"
        // title={() => (
        //   <Space>
        //     <ToggleAllButton
        //       openAll={openAllExpanded}
        //       closeAll={clearExpandedRowKeys}
        //     />
        //     {/* <MasterTableResourceGuard onlyEdit>
        //       <Button
        //         onClick={() => {
        //           setOpenModal(true);
        //         }}
        //       >
        //         {t('manage-exclusion-settings')}
        //       </Button>
        //     </MasterTableResourceGuard> */}
        //   </Space>
        // )}
        columns={[
          {
            title: t('organization-control-code'),
            dataIndex: 'code',
            width: TABLE_TREE_CODE_WIDTH,
            render: (value) => (
              <Text weight="regular" text={value} maxRows={1} />
            ),
          },
          {
            title: t('name'),
            dataIndex: 'name',
            render: (value, record) => (
              <OrganizationName
                name={value}
                isIntegrated={record.isIntegrated}
                isExcludedFromOptions={record.isExcludedFromOptions}
              />
            ),
          },
          {
            title: t('role-name'),
            dataIndex: 'roleName',
            render: (value) => <Text weight="regular" text={value} />,
          },
          {
            title: t('number-of-users'),
            dataIndex: 'userCount',
            width: 80,
            render: (value) => <Text weight="regular" text={value} />,
          },
          // {
          //   title: t('is-excluded-from-options'),
          //   dataIndex: 'isExcludedFromOptions',
          //   width: 120,
          //   render: (isExcluded) => <IsExcludedText isExcluded={isExcluded} />,
          // },
          {
            title: t('start-fiscal-year'),
            dataIndex: 'startYear',
            width: TABLE_YEAR_WIDTH,
            render: (value) => <Text weight="regular" text={value} />,
          },
          {
            title: t('end-fiscal-year'),
            dataIndex: 'endYear',
            width: TABLE_YEAR_WIDTH,
            render: (value) => <Text weight="regular" text={value} />,
          },
          {
            title: t('created-datetime'),
            dataIndex: 'createdAt',
            width: TABLE_DATETIME_WIDTH,
            render: (datetime) => (
              <Text weight="regular" text={datetimeFormat({ datetime })} />
            ),
          },
          {
            title: t('updated-datetime'),
            dataIndex: 'updatedAt',
            width: TABLE_DATETIME_WIDTH,
            render: (datetime) => (
              <Text weight="regular" text={datetimeFormat({ datetime })} />
            ),
          },

          {
            title: '',
            key: 'actions',
            width: TABLE_BUTTON_WIDTH,
            render: (_, record) => (
              <NavigateButton
                type="default"
                path={`organizations/${record.id}`}
                text={t('details')}
                asLink
              />
            ),
          },
        ]}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            onExpand(expanded, record.id);
          },
        }}
        pagination={{
          pageSize: 5,
          position: ['bottomRight'],
          showSizeChanger: false,
          total: organizations.data.count,
          current: pageNumber > 0 ? pageNumber : 1,
          onChange: (val) => {
            setSearchParams(
              (prev) => {
                prev.set('page', val.toString());
                return prev;
              },
              { replace: true },
            );
          },
        }}
      />
      <BulkUpdateExcludedFlagsFormModal
        open={openModal}
        onSubmit={async (values) => {
          const res = await bulkUpdateExcludedFlags(values);
          await Promise.all([
            openMessage(
              'success',
              t('message-bulk-process-requested', {
                requestedCount: res.requestedCount,
              }),
            ),
            (async () => {
              setOpenModal(false);
            })(),
          ]);
        }}
        onCancel={() => {
          setOpenModal(false);
        }}
      />
    </>
  );
};
